import React, { useState } from 'react';
import FormAndCalloutBox from '../../../3_Organisms/FormAndCalloutBox/FormAndCalloutBox';
import parse from 'html-react-parser';
import Input from '../../../1_Atoms/Forms/Input/Input';
import Button from '../../../1_Atoms/Button/Button';
import FormGroup from '../../../2_Molecules/FormGroup/FormGroup';
import { handleRequest } from '../../../../utils/httpRequest';
import Form from '../../../2_Molecules/Form/Form';
import { languageController } from '../../../../controllers/language/languageController';
import { useForm } from '../../../../hooks/useForm';
import Textarea from '../../../1_Atoms/Forms/Textarea/Textarea';
import Checkbox from '../../../1_Atoms/Forms/Checkbox/Checkbox';
import { autoFormatPhoneNumber, formatDate, pushToGTMDataLayer } from '../../../../utils/helpers';
import CalloutBoxFull from '../../../2_Molecules/CalloutBox/CalloutBoxFull';
import { Location } from '../../../../types';

interface VehicleData {
  id: number;
  isEV: boolean;
  title: string;
  slug: string;
  model: string;
  msrp: number;
  year: string;
  modelYear?: string;
  description: string;
  visual: {
    jelly: string;
    jelly_hover: string;
    jelly_path: string;
    [key: string]: any;
  };
}

interface DealerInfo {
  name: string;
  dealer_code: string;
  [key: string]: any;
}

interface DealerData {
  dealer: DealerInfo;
  userPosition?: Location;
}

interface DateAndTimeData {
  date: Date | string;
  time: string;
}

interface ContactDetails {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  message: string;
  optin: string;
  phone: string;
}

interface FormResponse {
  Success: boolean;
  state?: string;
  Errors?: string[];
}

interface ContactDetailsStepProps {
  handleNextStep: (data: { contactDetails: ContactDetails }) => void;
  title?: string;
  submitButtonText?: string;
  dealerData?: DealerData | null;
  dateAndTimeData?: DateAndTimeData;
  vehicleData?: VehicleData | null;
  formSource?: string;
  formPid?: number;
  formEndPointUrl?: string;
  formLanguage?: string;
  formDisclaimer?: string;
  isPreorderModal?: boolean;
}

const ContactDetailsStep_PreOrder: React.FC<ContactDetailsStepProps> = ({
  handleNextStep,
  title,
  submitButtonText,
  dealerData,
  dateAndTimeData,
  vehicleData,
  formSource,
  formPid,
  formEndPointUrl,
  formLanguage,
  formDisclaimer,
  isPreorderModal
}) => {
  const [formError, setFormError] = useState<string | null>(null);

  const { dealer: selectedDealerData, userPosition } = dealerData || {};

  const initState = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    message: '',
    optin: 'off', // 'on' or 'off'
    phone: ''
  };

  const validations = {
    firstname: ['required', 'name', 'nameMaxMin'] as const,
    lastname: ['required', 'name', 'nameMaxMin'] as const,
    email: ['required', 'email'] as const,
    postalcode: ['required', 'postalcode'] as const,
  } as any;

  const { handleSubmit, handleChange, data: formData, errors, setData } = useForm({
    initState,
    validations,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const otherData: Record<string, any> = {
          source: formSource,
          language: formLanguage,
          pid: String(formPid)
        };

        // Add selectedDealerData data if passed
        if (selectedDealerData) {
          otherData.dealerid = selectedDealerData.dealer_code;
        }

        // Add vehicleData data if passed
        if (vehicleData) {
          otherData.year = vehicleData.year;
          otherData.model = vehicleData.model;
        }

        // Add Date and Time data if passed
        if (dateAndTimeData) {
          otherData.date = typeof dateAndTimeData.date === 'string' ? dateAndTimeData.date : formatDate(dateAndTimeData.date);
          otherData.time = dateAndTimeData.time;
        }

        // Remove whitespace in postalcode
        const formDataCopy = { ...formData };
        if (formDataCopy.postalcode) {
          formDataCopy.postalcode = String(formDataCopy.postalcode).replace(/ /g, "");
        }

        const res = await handleRequest<FormResponse>('post', formEndPointUrl || '', { ...formDataCopy, ...otherData });

        if (res && res.Success) {
          pushToGTMDataLayer({ formevent: 'submit' });
          handleNextStep({ contactDetails: formDataCopy as any as ContactDetails });
        } else {
          setFormError(res?.state ? res.state : (res?.Errors?.[0] || 'An error occurred'));
        }
      } catch (err) {
        console.log(err);
      }
    },
  });

  const { firstname, lastname, email, postalcode, message, optin, phone } = formData;

  const isFormFilled = (firstname && lastname && email && postalcode);

  return (
    <div className="mz-contact-details-step">
      <div className="mz-contact-details-step__top">
        <div className={(window as any).bnp_retailer_mode ? "" : "mz-container"}>
          <div className="mz-full-width">
            <h5 className="mz-contact-details-step__title">{title}</h5>
          </div>
        </div>
      </div>
      <FormAndCalloutBox>
        <FormAndCalloutBox.Form>
          <Form
            type="contact-details"
            onSubmit={handleSubmit}
            isDark={false}
            noValidate={true}
            requiredFieldsHelperText={true}
            errorMessage={formError || undefined}
          >
            <FormGroup md={2}>
              <Input
                type="text"
                name="firstname"
                label={languageController.getFormCopy('label', 'firstname')}
                value={String(firstname)}
                isRequired={true}
                errorMessage={errors.firstname}
                onChange={handleChange}
                maxLength={50}
              />
              <Input
                type="text"
                name="lastname"
                label={languageController.getFormCopy('label', 'lastname')}
                value={String(lastname)}
                isRequired={true}
                errorMessage={errors.lastname}
                onChange={handleChange}
                maxLength={50}
              />
            </FormGroup>
            <Input
              type="email"
              name="email"
              label={languageController.getFormCopy('label', 'email')}
              value={String(email)}
              isRequired={true}
              errorMessage={errors.email}
              onChange={handleChange}
              maxLength={320}
            />
            <FormGroup md={2}>
              <Input
                type="text"
                name="postalcode"
                label={languageController.getFormCopy('label', 'postalcode')}
                value={String(postalcode)}
                isRequired={true}
                errorMessage={errors.postalcode}
                onChange={handleChange}
                maxLength={7}
              />
              {/*<Input
                type="tel"
                name="phone"
                label={languageController.getFormCopy('label', 'phone')}
                value={String(phone)}
                isRequired={false}
                errorMessage={errors.phone}
                onChange={(e) => {
                  setData({ ...formData, phone: autoFormatPhoneNumber(e.target.value) });
                }}
                maxLength={17}
              />*/}
            </FormGroup>
            <Textarea
              name="message"
              label={languageController.getFormCopy('label', 'message2500')}
              value={String(message)}
              isRequired={false}
              errorMessage={errors.message}
              onChange={handleChange}
              maxLength={2500}
              rows={5}
            />
            <p className="form-disclaimer-introduction">
              {languageController.getCopy('global', 'emailEncouragement')}
            </p>
            <Checkbox
              name="optin"
              checked={(optin === 'on')}
              onChange={(e) => setData({ ...formData, optin: optin === 'off' ? 'on' : 'off' })}
              label={formDisclaimer ? parse(formDisclaimer) : ''}
            />
            <Button
              type="submit"
              className="mz-btn--primary mz-btn--red"
              label={submitButtonText}
              disabled={!isFormFilled}
            />
          </Form>
        </FormAndCalloutBox.Form>
        <FormAndCalloutBox.Callout>
          <CalloutBoxFull
            vehicleData={vehicleData as any}
            dealerData={selectedDealerData as any}
            userPosition={userPosition}
            dateAndTimeData={dateAndTimeData as any}
            displayPrice={false}
            isProrderModal={isPreorderModal}
          />
        </FormAndCalloutBox.Callout>
      </FormAndCalloutBox>
    </div>
  );
};

export default ContactDetailsStep_PreOrder;
